import { useEffect, useState } from 'react';
import {
  MagnifyingGlass,
  CaretDown,
  CaretRight,
  Funnel,
} from '@phosphor-icons/react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useTableOperation } from '@/features/explore/api/tableOperation';
import { useTableOperation as useReportsTableOperation } from '@/features/reports/api/tableOperation';
import { ClickAwayListener, Fade, Popper } from '@mui/material';
import { useRef } from 'react';
import Skeleton from '@/components/Skeleton';
import { isEqual, map, uniq } from 'lodash';
import Button from '@/components/Button';
import Input from '@/components/Input';
import dayjs from 'dayjs';
import Checkbox from '@/components/Checkbox';

export const getTimestamp = (item, schema, columnName) => {
  let ts = Number(item);
  if (
    !isNaN(ts) &&
    schema?.[columnName]?.formatMeta?.timestampFormat === 'DATETIME'
  ) {
    return dayjs.unix(item).format('YYYY-MM-DD HH:mm:ss');
  } else if (
    !isNaN(ts) &&
    schema?.[columnName]?.formatMeta?.timestampFormat === 'DATE'
  ) {
    return dayjs.unix(item).format('YYYY-MM-DD');
  } else {
    return item;
  }
};
export const checkIsArray = (item) => {
  let flag = false;
  if (item.length === 1) {
    flag = typeof item[0] === 'string';
    return flag;
  } else {
    return item.some((data, index) => {
      if (index + 1 === item.length || index === 2) {
        return flag;
      } else {
        flag = typeof data === 'string';
      }
    });
  }
};
export const getPostProcessing = (item, schema, columnName) => {
  return schema?.[columnName]?.format === 'currency'
    ? typeof item === 'string'
      ? !isNaN(item?.[0])
        ? schema?.[columnName]?.formatMeta?.currencyType
          ? schema?.[columnName]?.formatMeta?.currencyType +
            ' ' +
            Number(item).toLocaleString('en-US')
          : Number(item).toLocaleString('en-US')
        : item
      : typeof item === 'number'
        ? schema?.[columnName]?.formatMeta?.currencyType
          ? schema?.[columnName]?.formatMeta?.currencyType +
            ' ' +
            item.toLocaleString('en-US')
          : item.toLocaleString('en-US')
        : item
    : schema?.[columnName]?.isTimestamp
      ? getTimestamp(item, schema, columnName)
      : schema?.[columnName]?.type === 'JSON' &&
          schema?.[columnName]?.format === 'multienum'
        ? Array.isArray(item)
          ? checkIsArray(item)
            ? item.join(', ')
            : item
          : item
        : item;
};

export const FilterDropDownPillTableNew = ({
  data = [],
  defaultSelectedOptions = [],
  caretRight = true,
  onApply = () => {},
  allowEmpty = false,
  onResetClick,
  tableOps,
  columnName,
  actualTableRowCount,
  clickedFilter,
  setFilterOpen,
  schema,
  msgId,
  stepId,
  name,
  headerId,
  from,
}) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryType, setSearchQueryType] = useState();
  const [allowsEmpty, setAllowEmpty] = useState(allowEmpty);
  const [filterData, setFilterData] = useState(data);
  const [anchorEl, setAnchorEl] = useState();
  const [open, setOpen] = useState(false);
  const arrowBtnRef = useRef();
  const id = `th-nested-filter-${msgId}-${name}-${stepId}-${headerId}-${columnName}`;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget || event.current);
    setOpen(true);
    setFilterOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFilterOpen(false);
  };
  useEffect(() => {
    if (defaultSelectedOptions?.length) {
      setSelectedOptions([...defaultSelectedOptions]);
    }
    typeof filterData[0] && setSearchQueryType(typeof filterData[0]);
  }, []);

  const isChecked = (value) => {
    return selectedOptions?.includes(value);
  };
  useEffect(() => {
    if (clickedFilter === columnName) {
      handleClick(arrowBtnRef);
    }
  }, [columnName, clickedFilter]);
  const handleChange = (value) => {
    if (isChecked(value)) {
      setSelectedOptions((prev) => {
        return prev.filter((item) => item !== value);
      });
    } else {
      const item = filterData.find((e) => e === value);
      setSelectedOptions((prev) => [...prev, item]);
    }
  };

  const agentOperation = location.pathname.includes('explore')
    ? useReportsTableOperation({
        config: { enabled: false },
      })
    : useTableOperation({
        config: { enabled: false },
      });

  const fetchFilterResults = async () => {
    let clonedData = structuredClone(tableOps.current);
    let value =
      searchQueryType === 'number'
        ? Number(searchQuery)
        : searchQueryType === 'boolean'
          ? searchQuery.toLowerCase() === 'true'
          : searchQuery;
    let dataToSend = {
      conversationId: clonedData.conversationId,
      messageId: clonedData.messageId,
      name: clonedData?.name,
      stepId: clonedData?.stepId,
      operation: [{ name: 'search', field: columnName, value: [value] }],
    };
    let result = await agentOperation.mutateAsync(dataToSend);
    if (searchQuery !== '')
      setFilterData(uniq(map(result.data, columnName).flat()));
  };

  useEffect(() => {
    if (searchQuery !== '') {
      const delayDebounceFn = setTimeout(() => {
        if (actualTableRowCount > 100) fetchFilterResults();
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    } else {
      setFilterData(data);
    }
  }, [searchQuery]);
  useEffect(() => {
    if (filterData.length === 0 && data.length >= 0 && searchQuery === '') {
      setFilterData(data);
    }
  }, [filterData]);

  const options = filterData.filter((row) =>
    typeof row === 'string'
      ? row?.toLowerCase().includes(searchQuery.toLowerCase())
      : String(row).includes(searchQuery)
  );

  return (
    <div
      className="flex items-center justify-center -mx-4 -my-3 bg-transparent"
      style={{ width: 'calc(100% + 32px)' }}
      onMouseEnter={handleClick}
      onMouseLeave={(e) => {
        if (
          arrowBtnRef.current &&
          arrowBtnRef.current instanceof Node &&
          e.relatedTarget instanceof Node &&
          !arrowBtnRef.current.contains?.(e?.relatedTarget || null)
        ) {
          handleClose();
        }
      }}
    >
      <>
        <Button
          ref={arrowBtnRef}
          className={{
            button: {
              root: twMerge(
                'w-full px-4 py-3 hover:bg-transparent text-sm text-brand-ai-200 h-fit leading-5.5 active:text-inherit',
                from === 'dashboard' && 'text-xs'
              ),
            },
          }}
        >
          <div className={twMerge(`w-full flex justify-between items-center`)}>
            <Funnel
              size={from === 'dashboard' ? 15 : 16}
              className="text-pv-neutral-grey-900"
            />
            <span className="flex-1 text-left ml-2">Filter</span>
            <div className="text-pv-neutral-grey-900">
              {caretRight ? (
                <CaretRight
                  size={from === 'dashboard' ? 15 : 16}
                  className="text-inherit"
                />
              ) : (
                <CaretDown size={16} className="text-inherit" />
              )}
            </div>
          </div>
        </Button>
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          transition
          placement="right-start"
          disablePortal={true}
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: false,
                rootBoundary: 'viewport',
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: false,
                altBoundary: false,
                tether: false,
                rootBoundary: 'document',
              },
            },
            {
              name: 'arrow',
              enabled: false,
              options: {
                element: arrowBtnRef,
              },
            },
          ]}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <div
                className="bg-white rounded-lg shadow-popup"
                style={{ width: '250px' }}
              >
                <ClickAwayListener
                  onClickAway={(e) => {
                    if (!arrowBtnRef.current?.contains?.(e.target)) {
                      handleClose(e);
                    }
                  }}
                >
                  <div className="text-left">
                    <Input
                      type={
                        typeof filterData[0] === 'number' ? 'number' : 'text'
                      }
                      leftElem={
                        <MagnifyingGlass
                          size={12}
                          className="text-pv-neutral-grey-600"
                        />
                      }
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                      showClearInput={true}
                      clearInput={() => setSearchQuery('')}
                      className={{
                        wrapper: 'p-3 pb-1.5',
                        input: {
                          wrapper: 'p-2',
                          root: 'text-xs',
                          clear: 'text-pv-neutral-grey-600',
                        },
                      }}
                    />
                    {searchQuery && (
                      <>
                        <div className="text-brand-ai-gray-300 text-xs mx-3">
                          {agentOperation.isLoading
                            ? 'Searching'
                            : options.length > 0
                              ? 'Search results'
                              : 'No fields found'}{' '}
                          for{' '}
                          <span
                            className={twJoin(
                              'font-medium',
                              !agentOperation.isLoading && 'text-brand-ai-200'
                            )}
                          >
                            {!agentOperation.isLoading
                              ? `"${searchQuery}"`
                              : searchQuery}
                          </span>
                        </div>
                        {options.length === 0 && (
                          <Button
                            variant="secondary"
                            size="small"
                            className={{
                              button: { root: 'mx-3 mt-2 mb-1' },
                            }}
                            onClick={() => setSearchQuery('')}
                          >
                            Clear Search
                          </Button>
                        )}
                      </>
                    )}
                    <div className="overflow-y-auto max-h-48 mr-1">
                      {!agentOperation.isLoading ? (
                        <>
                          {options.map((item, index) => {
                            const label = getPostProcessing(
                              item,
                              schema,
                              columnName
                            );
                            return (
                              <Checkbox
                                id={index}
                                label={
                                  typeof label !== 'string'
                                    ? String(label)
                                    : label
                                }
                                displayTooltip={true}
                                displayTooltipOnOverflow={true}
                                className={{
                                  wrapper:
                                    'w-full px-4 py-3 gap-3 cursor-pointer hover:bg-pv-primary-primary-50 cursor-pointer',
                                  label:
                                    'flex-1 overflow-hidden overflow-ellipsis whitespace-nowrap',
                                }}
                                checked={isChecked(item)}
                                onChange={() => handleChange(item)}
                              />
                            );
                          })}
                        </>
                      ) : (
                        <div>
                          {Array.from({ length: 4 }).map((_, i) => (
                            <div className="flex gap-3 px-4 py-3">
                              <Skeleton
                                width={16}
                                height={16}
                                className="bg-brand-ai-gray-400 rounded-md"
                              />
                              <Skeleton
                                width={(i + 1) % 2 ? '80%' : '70%'}
                                height={16}
                                className="bg-brand-ai-gray-400 rounded-md"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="px-3 py-1.5 border-t border-pv-neutral-grey-50 flex items-center justify-between">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setAllowEmpty(true);
                          setSelectedOptions([]);
                        }}
                        disabled={selectedOptions?.length < 1}
                      >
                        Clear Filter
                      </Button>
                      <Button
                        variant="primary"
                        className={{
                          button: {
                            root: 'px-3',
                          },
                        }}
                        disabled={
                          allowsEmpty
                            ? false
                            : isEqual(selectedOptions, defaultSelectedOptions)
                        }
                        onClick={() => onApply(selectedOptions)}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </ClickAwayListener>
              </div>
            </Fade>
          )}
        </Popper>
      </>
    </div>
  );
};
